import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { AddFormElements, Fieldset, Input, Row, TextParam } from '../../../_lib/Form2';
import { ButtonMinorAction } from '../../../components/molecules/Btn/ButtonWrapper';
import { Icons } from '../../../components/atoms/SVG';
import { Step } from '../../../components/molecules/Form/components/MultiStepForm';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useTabBarContext } from '../../../components/molecules/Tabs';
import usePrevious from '../../../hooks/usePrevious';

type TicketModalMultiStepFormCheckListStepProps = {
  checkListTemplateInputParam: TextParam<'checkListItem'>;
  submitButton: ReactNode;
};

export function TicketModalMultiStepFormCheckListStep({
  checkListTemplateInputParam,
  submitButton,
}: TicketModalMultiStepFormCheckListStepProps) {
  const { t } = useSonTranslation('TicketBooking');
  const { isActiveItem } = useTabBarContext();
  const [checkListInputs, setCheckListInputs] = useState([{ ...checkListTemplateInputParam }]);
  const prevCheckListInputs = usePrevious(checkListInputs);
  const lastInputRef = useRef<HTMLInputElement>(null);

  const id = 'ticket-add-modal-form-check-list';

  // focus on last input => works for add & delete
  useEffect(() => {
    if (lastInputRef.current && isActiveItem(id) && checkListInputs.length !== prevCheckListInputs?.length) {
      lastInputRef.current.focus();
    }
  }, [checkListInputs.length, isActiveItem, prevCheckListInputs?.length]);

  return (
    <Step id={id} buttonLabel={t('edit.form.steps.checkList')} submitButton={submitButton}>
      {
        // fieldset is controlled by +-button below (see aria-controls)
      }
      <Fieldset id="checklist-inputs" legend={t('edit.form.fieldsetLegendCheckList')}>
        {checkListInputs.map((item, index, array) => (
          <Row key={`${item.name}-${index}`}>
            <Input
              ref={index === checkListInputs.length - 1 ? lastInputRef : undefined}
              required
              param={item}
              value={item.value || ''}
              onChange={event => {
                item.value = event.target.value;
                setCheckListInputs([...array]);
              }}
              label={t('edit.form.labelCheckListInput', { number: index + 1 })}
            />
            {index >= 1 ? (
              <div className="ticket-booking__x-button">
                <ButtonMinorAction
                  text={t('edit.form.labelDeleteButtonCheckListInput', { number: index + 1 })}
                  icon={Icons.DECLINE_CROSS}
                  onClick={() => setCheckListInputs(array.toSpliced(index, 1))}
                />
              </div>
            ) : null}
          </Row>
        ))}
      </Fieldset>
      {/* 50 checklist elements only */}
      {checkListInputs.length <= 50 ? (
        <AddFormElements
          name={t('edit.form.labelAddCheckListItem')}
          aria-controls="checklist-inputs"
          onClick={() => {
            if (checkListInputs.length <= 50) {
              setCheckListInputs([...checkListInputs, { ...checkListTemplateInputParam }]);
            }
          }}
        />
      ) : null}
    </Step>
  );
}
